// Web Push (HARDEN-pwa; 06 §Notification preferences, the `desktop` channel). The caller's own
// browsers in this company: the server's push configuration (VAPID public key, or why push is
// off), and subscribing or unsubscribing the current browser. Which inbox items reach a
// subscribed browser stays the notification preferences' decision (`inbox_item.desktop`, quiet
// hours); delivery is the `push.inbox` consumer. Core routes, like the inbox: every person who
// has an inbox may subscribe.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** base64url without padding (the PushSubscription JSON form of keys). */
const Base64Url = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .regex(/^[A-Za-z0-9_-]+$/);

/** One of the caller's subscribed browsers, without its endpoint (a capability URL). */
export const PushSubscriptionView = z
  .object({
    id: Uuid,
    /** SHA-256 of the endpoint (hex): the browser compares its own to know it is subscribed. */
    endpoint_sha256: z.string(),
    /** The push service's host (`fcm.googleapis.com`, `web.push.apple.com`, …). */
    push_service: z.string(),
    created_at: Timestamp,
    last_success_at: Timestamp.nullable(),
  })
  .meta({ id: "PushSubscriptionView" });

export const PushConfig = z
  .object({
    /** False when the server has no VAPID keys: the browser toggle is off with `reason`. */
    enabled: z.boolean(),
    /** The VAPID public key (base64url, 65 bytes uncompressed P-256) the browser subscribes with. */
    public_key: z.string().nullable(),
    /** Why push is off (null when enabled). */
    reason: z.string().nullable(),
    /** The caller's subscribed browsers in this company (none for a principal without an inbox). */
    subscriptions: z.array(PushSubscriptionView),
  })
  .meta({ id: "PushConfig" });

/** The browser's `PushSubscription.toJSON()`. */
export const PushSubscribe = z
  .strictObject({
    endpoint: z.url().max(2048),
    expiration_time: z.int().min(0).nullable().optional(),
    keys: z.strictObject({
      /** 65-byte uncompressed P-256 point: 87 base64url characters. */
      p256dh: Base64Url(100),
      /** 16-byte secret: 22 base64url characters. */
      auth: Base64Url(32),
    }),
  })
  .meta({ id: "PushSubscribe" });

export const PushUnsubscribe = z
  .strictObject({ endpoint: z.url().max(2048) })
  .meta({ id: "PushUnsubscribe" });

export const pushRoutes = {
  "push.config": defineRoute({
    ...core,
    id: "push.config",
    method: "get",
    path: `${base}/my/push`,
    tag: "push",
    summary:
      "Web Push for the caller in this company: the VAPID public key or why push is off, and the caller's subscribed browsers (HARDEN)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: PushConfig },
    errors: [],
    emits: [],
  }),
  "push.subscribe": defineRoute({
    ...core,
    id: "push.subscribe",
    method: "put",
    path: `${base}/my/push/subscription`,
    tag: "push",
    summary:
      "Subscribe the current browser to the caller's desktop notifications in this company (HARDEN)",
    access: "member",
    params: CompanyParams,
    body: PushSubscribe,
    response: { status: 200, schema: PushSubscriptionView },
    errors: ["push_disabled"],
    emits: ["push_subscription.added"],
  }),
  "push.unsubscribe": defineRoute({
    ...core,
    id: "push.unsubscribe",
    method: "post",
    path: `${base}/my/push/unsubscribe`,
    tag: "push",
    summary: "Stop desktop notifications to the current browser in this company (HARDEN)",
    access: "member",
    params: CompanyParams,
    body: PushUnsubscribe,
    response: { status: 204 },
    errors: [],
    emits: ["push_subscription.removed"],
  }),
} as const;
